import Container from "../../shared/ui/Container";
import "./AccessibilityStatement.scss";

export default function AccessibilityStatement() {
  return (
    <div className="accessibility-statement">
      <section className="page-header" aria-labelledby="page-title">
        <Container>
          <h1 id="page-title" className="page-header__title">
            Accessibility Statement
          </h1>
          <p className="page-header__subtitle">
            Last updated: November 17, 2025
          </p>
        </Container>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="commitment-title"
      >
        <h2 className="accessibility-section__title">
          1. Our Commitment to Accessibility
        </h2>
        <p className="accessibility-section__text">
          We are committed to ensuring digital accessibility for people with
          disabilities. We are continually improving the user experience for
          everyone and applying the relevant accessibility standards to achieve
          these goals.
        </p>
        <p className="accessibility-section__text">
          Our goal is to make our website accessible to the widest possible
          audience, regardless of technology or ability. We actively work to
          increase the accessibility and usability of our website and in doing
          so adhere to many of the available standards and guidelines.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="standards-title"
      >
        <h2 className="accessibility-section__title">
          2. Accessibility Standards
        </h2>
        <p className="accessibility-section__text">
          This website aims to conform to the following accessibility standards:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            <strong>WCAG 2.1 Level AA</strong> — Web Content Accessibility
            Guidelines (WCAG) 2.1 at Level AA
          </li>
          <li className="accessibility-section__list-item">
            <strong>AODA</strong> — Accessibility for Ontarians with
            Disabilities Act
          </li>
          <li className="accessibility-section__list-item">
            <strong>Section 508</strong> — U.S. federal accessibility
            requirements
          </li>
          <li className="accessibility-section__list-item">
            <strong>ADA</strong> — Americans with Disabilities Act standards
          </li>
        </ul>
        <p className="accessibility-section__text">
          We are working towards full compliance with these standards and
          continuously improving our website's accessibility.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="features-title"
      >
        <h2 className="accessibility-section__title">
          3. Current Accessibility Features
        </h2>
        <p className="accessibility-section__text">
          Our website includes the following accessibility features:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            Semantic HTML structure for better screen reader navigation
          </li>
          <li className="accessibility-section__list-item">
            Alternative text (alt text) for images where applicable
          </li>
          <li className="accessibility-section__list-item">
            Keyboard navigation support throughout the website
          </li>
          <li className="accessibility-section__list-item">
            Focus indicators for keyboard users
          </li>
          <li className="accessibility-section__list-item">
            Proper heading hierarchy for content structure
          </li>
          <li className="accessibility-section__list-item">
            ARIA labels and roles where appropriate
          </li>
          <li className="accessibility-section__list-item">
            Color contrast that meets WCAG AA standards
          </li>
          <li className="accessibility-section__list-item">
            Responsive design that works on various devices and screen sizes
          </li>
          <li className="accessibility-section__list-item">
            Forms with proper labels and error messages
          </li>
        </ul>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="limitations-title"
      >
        <h2 className="accessibility-section__title">
          4. Known Limitations and Areas for Improvement
        </h2>
        <p className="accessibility-section__text">
          We are aware that some parts of our website may not be fully
          accessible. We are actively working to address the following areas:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            Some images may need more descriptive alternative text
          </li>
          <li className="accessibility-section__list-item">
            Some interactive elements may need enhanced keyboard navigation
          </li>
          <li className="accessibility-section__list-item">
            Some content may benefit from additional ARIA labels
          </li>
          <li className="accessibility-section__list-item">
            Video content (if any) may need captions and transcripts
          </li>
          <li className="accessibility-section__list-item">
            Some forms may need additional validation feedback
          </li>
        </ul>
        <p className="accessibility-section__text">
          We are committed to addressing these issues and improving the overall
          accessibility of our website. We welcome your feedback to help us
          prioritize these improvements.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="assistive-title"
      >
        <h2 className="accessibility-section__title">
          5. Assistive Technologies
        </h2>
        <p className="accessibility-section__text">
          Our website is designed to work with the following assistive
          technologies:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            Screen readers (JAWS, NVDA, VoiceOver, TalkBack)
          </li>
          <li className="accessibility-section__list-item">
            Screen magnification software
          </li>
          <li className="accessibility-section__list-item">
            Voice recognition software
          </li>
          <li className="accessibility-section__list-item">
            Keyboard-only navigation
          </li>
          <li className="accessibility-section__list-item">
            Switch control devices
          </li>
        </ul>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="feedback-title"
      >
        <h2 className="accessibility-section__title">
          6. Feedback and Reporting Issues
        </h2>
        <p className="accessibility-section__text">
          We welcome your feedback on the accessibility of our website. If you
          encounter any accessibility barriers or have suggestions for
          improvement, please contact us:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            <strong>Email:</strong> contactus@dafi.ca
          </li>
          <li className="accessibility-section__list-item">
            <strong>Contact Form:</strong>{" "}
            <a href="/contact" className="accessibility-section__link">
              Visit our Contact Page
            </a>
          </li>
        </ul>
        <p className="accessibility-section__text">
          When reporting an accessibility issue, please include:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            The URL or page where you encountered the issue
          </li>
          <li className="accessibility-section__list-item">
            A description of the problem
          </li>
          <li className="accessibility-section__list-item">
            The assistive technology you were using (if applicable)
          </li>
          <li className="accessibility-section__list-item">
            Your contact information (optional, but helpful for follow-up)
          </li>
        </ul>
        <p className="accessibility-section__text">
          We aim to respond to accessibility feedback within 2-5 business days.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="testing-title"
      >
        <h2 className="accessibility-section__title">
          7. Testing and Evaluation
        </h2>
        <p className="accessibility-section__text">
          We regularly test our website for accessibility using:
        </p>
        <ul className="accessibility-section__list">
          <li className="accessibility-section__list-item">
            Automated accessibility testing tools
          </li>
          <li className="accessibility-section__list-item">
            Manual testing with assistive technologies
          </li>
          <li className="accessibility-section__list-item">
            User testing with people who have disabilities
          </li>
          <li className="accessibility-section__list-item">
            Expert accessibility audits
          </li>
        </ul>
        <p className="accessibility-section__text">
          We are committed to ongoing evaluation and improvement of our
          website's accessibility.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="updates-title"
      >
        <h2 className="accessibility-section__title">
          8. Updates to This Statement
        </h2>
        <p className="accessibility-section__text">
          We will review and update this accessibility statement regularly to
          reflect our ongoing commitment and progress. This statement was last
          updated on November 17, 2025.
        </p>
      </section>

      <section
        className="accessibility-section"
        aria-labelledby="contact-title"
      >
        <h2 className="accessibility-section__title">9. Contact Us</h2>
        <p className="accessibility-section__text">
          If you have questions or concerns about the accessibility of our
          website, or if you need assistance accessing any content, please
          contact us:
        </p>
        <p className="accessibility-section__text">
          <strong>Email:</strong> contactus@dafi.ca
        </p>
        <p className="accessibility-section__text">
          <strong>Contact Form:</strong>{" "}
          <a href="/contact" className="accessibility-section__link">
            Visit our Contact Page
          </a>
        </p>
        <p className="accessibility-section__text">
          We are committed to making our website accessible to everyone and
          appreciate your patience as we continue to improve.
        </p>
      </section>
    </div>
  );
}
